/**
 * Carbon inline notification, low contrast (DESIGN.md "Cards / Containers"):
 * layer-01 with a 1px border mixed 40% from the status color, a 3px leading
 * bar, a 20px status icon, a 600 title and a secondary-text body.
 *   error  Coral and the octagon. Blocking errors only (the Neutral Bad State
 *          Rule); role="alert".
 *   info   The neutral info tone (D17) and the info circle; role="status".
 * `actions` sits after the text (a link such as "Finish setup"); `onClose`
 * adds a dismiss button.
 */
import clsx from 'clsx'
import { IconButton } from './IconButton'
import { IconClose, IconInfo, IconSeverityCrit } from './icons'
import type { InlineNotificationProps } from './types'

const TONE: Record<InlineNotificationProps['kind'], string> = {
  error: '[--nc:var(--sev-crit)]',
  info: '[--nc:var(--info)]',
}

export function InlineNotification({
  kind,
  title,
  children,
  actions,
  onClose,
  closeLabel = 'Dismiss notification',
  className,
}: InlineNotificationProps) {
  const Icon = kind === 'error' ? IconSeverityCrit : IconInfo
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      className={clsx(
        'relative flex items-start gap-4 border bg-layer-1 pl-4 text-body text-text-primary',
        'border-[color:color-mix(in_oklab,var(--nc)_40%,transparent)]',
        TONE[kind],
        // With a dismiss button the 48px button sets the row height; the text pads itself.
        onClose ? 'py-0 pr-0' : 'py-3.5 pr-4',
        className,
      )}
    >
      {/* The 3px leading bar in the status color. */}
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-[color:var(--nc)]" />
      <Icon size={20} className={clsx('text-[color:var(--nc)]', onClose && 'mt-3.5')} />
      <div className={clsx('flex min-w-0 flex-1 flex-wrap items-baseline gap-x-4 gap-y-2', onClose && 'py-3.5')}>
        <p className="m-0 min-w-0">
          <b className="mr-1 font-semibold">{title}</b>
          {children ? <span className="text-text-secondary">{children}</span> : null}
        </p>
        {actions ? <div className="flex flex-none items-center">{actions}</div> : null}
      </div>
      {onClose ? <IconButton label={closeLabel} icon={<IconClose />} size={48} onClick={onClose} /> : null}
    </div>
  )
}

export default InlineNotification
